import { useEffect, useState, useCallback } from "react";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard } from "@/components/shared";
import { Wallet, CalendarClock, AlertTriangle, Package, Receipt, Truck, Egg, Warehouse } from "lucide-react";

const PERIODS = [
  { key: "today", label: "Bugün" },
  { key: "month", label: "Bu Ay" },
  { key: "all", label: "Tüm Zamanlar" },
];

export default function SalesDashboard() {
  const { user } = useAuth();
  const canFin = user?.role === "admin" || !!user?.permissions?.view_financials;
  const [period, setPeriod] = useState("month");
  const [sum, setSum] = useState(null);
  const [recv, setRecv] = useState(null);

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([
      api.get(`/dashboard/sales-summary?period=${period}`),
      canFin ? api.get(`/dashboard/receivables`) : Promise.resolve({ data: null }),
    ]);
    setSum(s.data);
    setRecv(r.data);
  }, [period, canFin]);
  useEffect(() => { load(); }, [load]);

  const tl = (v) => (Number(v) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " TL";
  const periodLabel = PERIODS.find((p) => p.key === period)?.label;

  return (
    <div data-testid="sales-dashboard">
      <PageHeader
        title="Kontrol Paneli"
        subtitle="Kümes satış özeti · cari alacaklar ve satılan karton bilgileri"
        action={
          <div className="inline-flex rounded-lg border border-border bg-card p-1" data-testid="sales-period-group">
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`sales-period-${p.key}`}
                className={`px-3 h-8 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${period === p.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
                {p.label}
              </button>
            ))}
          </div>
        }
      />

      {/* Cari alacak kartları */}
      {canFin && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="sales-receivables-cards">
          <StatCard icon={Wallet} label="Toplam Alacak" value={tl(recv?.total_receivable)} sub="Tüm cari bakiyeler" tone="primary" testid="sales-recv-total" />
          <StatCard icon={CalendarClock} label="Bu Hafta Tahsilat" value={tl(recv?.due_this_week)} sub="Önümüzdeki 7 gün vadeli" tone="sky" testid="sales-recv-week" />
          <StatCard icon={AlertTriangle} label="Vadesi Geçen" value={tl(recv?.overdue)} sub={`${recv?.overdue_count || 0} satış`} tone="red" testid="sales-recv-overdue" />
        </div>
      )}

      {/* Satış KPI kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="sales-kpi-cards">
        <StatCard icon={Package} label={`Satılan Karton · ${periodLabel}`} value={fmt(sum?.total_cartons)} sub="Toplam kümesten çıkan" tone="primary" testid="sales-kpi-cartons" />
        {canFin && <StatCard icon={Receipt} label={`Satış Tutarı · ${periodLabel}`} value={tl(sum?.total_amount)} sub="Palet + ekstralar dahil" tone="sky" testid="sales-kpi-amount" />}
        <StatCard icon={Truck} label={`Satış Fişi · ${periodLabel}`} value={fmt(sum?.shipment_count)} sub="Düzenlenen fiş adedi" tone="amber" testid="sales-kpi-count" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Kümese göre satılan karton */}
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="sales-by-coop">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
            <Warehouse size={18} className="text-primary" />
            <h3 className="font-head font-semibold">Kümese Göre Satılan Karton</h3>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {(sum?.by_coop || []).length === 0 ? (
                <tr><td className="px-5 py-8 text-center text-muted-foreground">Bu dönemde satış yok.</td></tr>
              ) : sum.by_coop.map((r) => (
                <tr key={r.coop_id} className="border-t border-border" data-testid={`sales-coop-${r.coop_id}`}>
                  <td className="px-5 py-3 font-medium">{r.name}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-bold text-primary">{fmt(r.cartons)} <span className="text-xs font-normal text-muted-foreground">karton</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Yumurta boyuna göre satılan karton */}
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="sales-by-size">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
            <Egg size={18} className="text-primary" />
            <h3 className="font-head font-semibold">Yumurta Boyuna Göre Satılan Karton</h3>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {(sum?.by_size || []).length === 0 ? (
                <tr><td className="px-5 py-8 text-center text-muted-foreground">Bu dönemde satış yok.</td></tr>
              ) : sum.by_size.map((r) => (
                <tr key={r.size} className="border-t border-border" data-testid={`sales-size-${r.size}`}>
                  <td className="px-5 py-3 font-medium">{r.label}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-bold text-primary">{fmt(r.cartons)} <span className="text-xs font-normal text-muted-foreground">karton</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
